import React, { FC, useCallback, useState, RefObject, useMemo } from "react";
import { LayoutChangeEvent, Platform, ScrollView } from "react-native";
import {
  ALIGN,
  Box,
  Divider,
  INFOBOX_TYPE,
  InfoBox,
  Layout as AuroraLayout,
  SPACE,
  Spinner,
  View,
  useDevice,
} from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import {
  PayButton,
  PaymentFlow,
  PaymentFlowStrategies,
  PaymentInstrumentSelect,
  PaymentInstrumentSelectRef,
  Section,
} from "@lookiero/payments-front";
import { PromoComponent, SERVICE } from "@lookiero/promocodes-front";
import { PromoCode, PromoCodeError } from "@lookiero/promocodes-front/build/core/domain";
import { Layout, Text } from "@lookiero/sty-psp-ui";
import { Product } from "../../../../../domain/entities/product/Product";
import { OrderSummaryForCheckout } from "../../../../../domain/projections/OrderSummaryForCheckout";
import { ProductList } from "../../../components/productList/ProductList";
import { useFormatCurrency } from "../../../hooks/useFormatCurrency";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { User } from "../../../shared/types";
import { financial } from "../../../shared/util/financial/financial";
import { CheckoutFooter } from "../footer/CheckoutFooter/CheckoutFooter";
import { CheckoutHeader } from "../header/checkoutHeader/CheckoutHeader";
import { style as checkoutStyle } from "./Checkout.styles";
import { StockOutModal } from "./stockOutModal/StockOutModal";

interface CheckoutProps {
  readonly layout: Layout;
  readonly orderSummary: OrderSummaryForCheckout;
  readonly user: User;
  readonly isCheckoutDisabled: boolean;
  readonly paymentFlowRef: RefObject<PaymentFlowStrategies | null>;
  readonly paymentInstrumentSelectRef: RefObject<PaymentInstrumentSelectRef | null>;
  readonly shippingAddress: string;
  readonly isLoading: boolean;
  readonly stockOutModalVisible: boolean;
  readonly onBeforeRedirect: () => Promise<string>;
  readonly onClose: () => void;
  readonly onDeleteProduct: (product: Product) => void;
  readonly onErrorPromocode: (error: PromoCodeError) => void;
  readonly onPressCheckout: () => void;
  readonly onValidatePromocode: (response: PromoCode) => void;
  readonly onLoadStockOutModal: (stockoutItems: Product[]) => void;
  readonly onCloseStockoutModal: () => void;
  readonly onAcceptStockoutModalWhenEveryItemStockout: () => void;
  readonly onAcceptStockoutModalWhenSomeItemStockout: (itemIds: Product[]) => void;
}

const Checkout: FC<CheckoutProps> = ({
  layout: Layout,
  orderSummary,
  user,
  isCheckoutDisabled,
  paymentFlowRef,
  paymentInstrumentSelectRef,
  stockOutModalVisible,
  shippingAddress,
  isLoading,
  onBeforeRedirect,
  onClose,
  onDeleteProduct,
  onErrorPromocode,
  onPressCheckout,
  onValidatePromocode,
  onLoadStockOutModal,
  onCloseStockoutModal,
  onAcceptStockoutModalWhenEveryItemStockout,
  onAcceptStockoutModalWhenSomeItemStockout,
}) => {
  const { screen } = useDevice();
  const formatCurrency = useFormatCurrency();

  const checkoutHeaderText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_HEADER,
  });
  const checkoutDeliveryTimeText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_DELIVERY_TIME,
  });
  const checkoutHomeDeliveryText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_HOME_DELIVERY,
  });
  const checkoutTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_TITLE,
  });
  const checkoutTotalText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_TOTAL,
  });
  const checkoutItemText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_ITEM,
  });
  const checkoutItemsText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_ITEMS,
  });
  const checkoutDeliveryFreeCostText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_DELIVERY_FREE_COST,
  });
  const checkoutPromoInfoText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_INFO_PROMO,
  });
  const checkoutSummarySalesLinedescText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_SUMMARY_SALES_LINEDESC,
  });
  const checkoutSummaryBalanceLineText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_SUMMARY_BALANCE_LINE,
  });
  const orderFreeText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_FREE,
  });

  const style = useMemo(() => checkoutStyle(), []);

  const [footerHeight, setFooterHeight] = useState(0);
  const handleOnLayoutFooter = useCallback(
    (event: LayoutChangeEvent) => Platform.OS !== "web" && setFooterHeight(event.nativeEvent.layout.height),
    [],
  );

  const availableProducts = orderSummary.items.filter((item) => item.available);

  return (
    <Layout
      header={<CheckoutHeader title={checkoutHeaderText} onClose={onClose} />}
      scrollEnabled={false}
      style={{ header: style.header, scrollView: style.scrollView }}
    >
      {isLoading ? (
        <View style={style.spinnerWrapper}>
          <Spinner />
        </View>
      ) : (
        <>
          <ScrollView contentContainerStyle={{ paddingBottom: footerHeight }} showsVerticalScrollIndicator={false}>
            <InfoBox contentAlign={ALIGN.CENTER} text={checkoutDeliveryTimeText} type={INFOBOX_TYPE.SQUARED} />

            <AuroraLayout
              fullWidth={!screen.L}
              style={[style.contentWrapper, screen.L ? style.contentWrapperDesktop : style.contentWrapperMobile]}
            >
              <Box size={{ L: "2/3" }} style={screen.L ? style.leftColumnDektop : style.leftColumnMobile}>
                <View
                  style={[style.checkoutAddress, screen.L ? style.checkoutAddressDesktop : style.checkoutAddressMobile]}
                  testID="checkout-address"
                >
                  <Text level={3} variant="action">
                    {checkoutHomeDeliveryText}
                  </Text>

                  <Text ellipsizeMode="tail">{shippingAddress}</Text>
                </View>

                <View
                  style={screen.L ? style.paymentInstrumentDesktop : style.paymentInstrumentMobile}
                  testID="checkout-payment"
                >
                  <PaymentInstrumentSelect
                    ref={paymentInstrumentSelectRef as RefObject<PaymentInstrumentSelectRef>}
                    beforeRedirect={onBeforeRedirect}
                    hidePaymentMethods={[]}
                    section={Section.DIRECT_BUY_CHECKOUT}
                    userInformation={{
                      name: user.shippingAddress.address.name,
                      email: user.email,
                      segment: user.segment,
                    }}
                    showSingleUsePaymentMethods
                  />
                </View>

                <View
                  style={screen.L ? style.promocodeWrapperDesktop : style.promocodeWrapperMobile}
                  testID="checkout-promocodes"
                >
                  <PromoComponent
                    promoCode={{ value: orderSummary.promo.code }}
                    service={SERVICE.SHOP}
                    hideButtonOnEmpty
                    showLink
                    onError={onErrorPromocode}
                    onValidate={onValidatePromocode}
                  />
                </View>

                <Text
                  level={3}
                  style={[style.checkoutTitle, screen.L ? style.checkoutTitleDesktop : style.checkoutTitleMobile]}
                  testID="checkout-title"
                  variant="heading"
                >
                  {checkoutTitleText}
                </Text>

                <ProductList
                  products={orderSummary.items}
                  style={style.productList}
                  showLastUnitsLabel
                  showLastUnitsStock
                  onDelete={onDeleteProduct}
                />
              </Box>

              <Box
                size={{ L: "1/3" }}
                style={[style.rightColumn, screen.L ? style.rightColumnDesktop : style.rightColumnMobile]}
              >
                <View wide>
                  <View style={style.containerPriceDetail}>
                    <Text level={3}>{`${availableProducts?.length} ${
                      availableProducts?.length === 1 ? checkoutItemText : checkoutItemsText
                    }`}</Text>

                    <Text level={3}>{formatCurrency({ amount: financial(orderSummary.grossPrice.amount) })}</Text>
                  </View>

                  {orderSummary.salesDiscount.amount > 0 ? (
                    <View style={style.containerPriceDetail}>
                      <Text level={3}>{checkoutSummarySalesLinedescText}</Text>

                      <Text level={3}>
                        {`-${formatCurrency({ amount: financial(orderSummary.salesDiscount.amount) })}`}
                      </Text>
                    </View>
                  ) : null}

                  {orderSummary.promo.code ? (
                    <View style={style.containerPriceDetail}>
                      <Text level={3}>
                        {orderSummary.promo.code} ({`-${orderSummary.promo.percentage}%`})
                      </Text>

                      <Text level={3}>
                        {`-${formatCurrency({ amount: financial(orderSummary.promo.discount.amount) })}`}
                      </Text>
                    </View>
                  ) : null}

                  {orderSummary.balance.amount > 0 ? (
                    <View style={style.containerPriceDetail}>
                      <Text level={3}>{checkoutSummaryBalanceLineText}</Text>

                      <Text level={3}>{`-${formatCurrency({ amount: financial(orderSummary.balance.amount) })}`}</Text>
                    </View>
                  ) : null}

                  <View style={style.containerPriceDetail}>
                    <Text level={3}>{checkoutDeliveryFreeCostText}</Text>

                    <Text level={3}>{orderFreeText}</Text>
                  </View>

                  <Divider marginBottom={SPACE.SPACE_4} />

                  <View style={[style.containerPriceDetail, !screen.L ? style.lastContainerPriceDetailMobile : null]}>
                    <Text level={2} variant="action">
                      {checkoutTotalText}
                    </Text>

                    <Text level={2} variant="action">
                      {formatCurrency({ amount: financial(orderSummary.netPrice.amount) })}
                    </Text>
                  </View>

                  {orderSummary.salesDiscount.amount > 0 && orderSummary.promo.code ? (
                    <Text level={2} style={style.promoInfo} variant="detail">
                      {checkoutPromoInfoText}
                    </Text>
                  ) : null}

                  {screen.L && (
                    <PayButton
                      disabled={isCheckoutDisabled}
                      section={Section.DIRECT_BUY_CHECKOUT}
                      testID="checkout-button"
                      onPress={onPressCheckout}
                    />
                  )}
                </View>
              </Box>
            </AuroraLayout>

            <PaymentFlow ref={paymentFlowRef} section={Section.DIRECT_BUY_CHECKOUT} withBalance />
          </ScrollView>

          {!screen.L && (
            <CheckoutFooter
              isCtaDisabled={isCheckoutDisabled}
              onLayout={handleOnLayoutFooter}
              onPressCheckout={onPressCheckout}
            />
          )}
        </>
      )}

      <StockOutModal
        orderSummary={orderSummary}
        visible={stockOutModalVisible}
        onAcceptWhenEveryItemStockout={onAcceptStockoutModalWhenEveryItemStockout}
        onAcceptWhenSomeItemStockout={onAcceptStockoutModalWhenSomeItemStockout}
        onClose={onCloseStockoutModal}
        onDismiss={onCloseStockoutModal}
        onLoad={onLoadStockOutModal}
      />
    </Layout>
  );
};

export type { CheckoutProps };
export { Checkout };
